import VideoPlayer from '@/components/_common/VideoPlayer/VideoPlayer';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { IngestFile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Paper, Button, useTheme } from '@mui/material';
import AudioPlayer from '@/components/_common/AudioPlayer';
import { ConfigContext } from '@/contextAPI/configContext';
import { useContext } from 'react';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OmakasePlayerClientConfig } from '@/__configs/config_types';
import VideoWithInventoryModal from '@/components/_common/VideoPlayerAdvanced/VideoWithInventoryModal';

interface FileIngestMediaProps {
  file: IngestFile;
}

const FileIngestMediaPreview = ({ file }: FileIngestMediaProps) => {
  const { file_type, filename } = file;
  const theme = useTheme();
  const clientConfig = useContext(ConfigContext);
  const disableVideoPreview = clientConfig?.dataConfig?.fileIngest?.disableVideoPreview || false;
  const config = useConfigValue<OmakasePlayerClientConfig>(['omakasePlayerConfig']);
  const hasAdvancedVideoFeaturesEnabled = !!config?.features?.length;
  const videoPosterOverride = clientConfig?.theme?.logo || theme?.preview_placeholder;

  const displayFile = () => {
    switch (file_type) {
      case 'image':
        return (
          <Poster>
            <ImageBackground url={file.media_url || ''} />
            <ImageHolder>
              <img src={file.media_url} alt={filename} />
            </ImageHolder>
          </Poster>
        );
      case 'audio':
        return <AudioPlayer url={file.media_url || ''} />;
      case 'subtitle':
        return (
          <div style={{ textAlign: 'center' }}>
            <div>No preview available.</div>
            <SubtitleDownloadLink href={file.media_url} download={filename} target="_blank">
              <Button variant="outlined" color="info" startIcon={<PolarisIcon icon="download" />}>
                Download Subtitle
              </Button>
            </SubtitleDownloadLink>
          </div>
        );
      case 'video':
        if (!disableVideoPreview) {
          if (hasAdvancedVideoFeaturesEnabled) {
            return <VideoWithInventoryModal url={file.media_url} poster={videoPosterOverride} autoplay={false} />;
          } else {
            return <VideoPlayer url={file.media_url} poster={videoPosterOverride} autoplay={false} />;
          }
        }

      default:
        return <>No preview available.</>;
    }
  };

  return <PreviewContainer>{displayFile()}</PreviewContainer>;
};

export default FileIngestMediaPreview;

const PreviewContainer = styled(Paper)`
  min-height: 300px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Poster = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
  overflow: hidden;
  border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`;

const ImageBackground = styled.div<{ url: string }>`
  height: 100%;
  width: 100%;
  inset: -10px;
  background: ${({ url }) => `url(${url}) center center / cover no-repeat`};
  filter: blur(5px) saturate(0.8);
  opacity: 0.5;
`;

const ImageHolder = styled.div`
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;

  img {
    max-width: 90%;
    max-height: 90%;
    display: block;
    transform: translate(-50%, -50%);
    border: ${({ theme }) => `2px solid ${theme.palette.divider + '8a'}`};
    box-shadow: ${({ theme }) => theme.shadows[10]};
  }
`;

const SubtitleDownloadLink = styled.a`
  display: inline-block;
  margin-top: 20px;
  text-decoration: none;
  & > button {
    color: ${({ theme }) => theme.palette.info.main};
  }
`;
